import { all, fork, takeLatest } from 'typed-redux-saga/macro';

import { OPEN_DOCUMENT } from '../actions';
import { handleOpenDocument } from './openDocument';
import { autoSaveSaga, saveAllDocumentsSaga } from './autoSave';
import { panelPreferencesSaga } from './panelPreferences';
import { suspendedLocksSaga } from './suspendedLocks';
import { keywordSearchSaga } from './keywordSearch';
import { lexiconApplySaga } from './lexiconApply';
import { pdfPrepassSaga } from './pdfPrepass';
import { pdfDimsPrepassSaga } from './pdfDimsPrepass';
import {
  onDeleteRedactedDocumentRequest,
  onDownloadRedactionReport,
  onIncrementRedactedDocumentDownload,
  onLoadRedactedVersionPages,
} from './exportPush';
import { auditEmitSaga } from './auditEmit';
import { auditLogsSaga } from './auditLogs';
import { redactionStatusSaga } from './redactionStatus';
import { renameSaga } from './rename';
import lexiconAutoApplySaga from './lexiconAutoApply';
import watermarkAutoApplySaga from './watermarkAutoApply';
import watermarkDeriveSaga from './watermarkDerive';
import watermarkImageAssetSaga from './watermarkImageAsset';
import { documentCommentsSaga } from './comments';
import { runPiiDetectionSaga } from './runPii';
import { ocrStatusSaga } from './ocrStatus';
import { engineStatusSaga } from './engineStatus';
import {
  fetchExternalIntegrationConfigSaga,
  sendToCasepointSaga,
  sendToExterroSaga,
  sendToExternalIntegrationSaga,
  sendToFOIAXpressSaga,
  sendToGovQaSaga,
  sendToNuixSaga,
} from './sendToIntegration';

/**
 * Root saga for the documents module.
 *
 * Registered in `src/common-app/state/sagas/index.ts` alongside other
 * app-wide sagas so it's always live, regardless of which route is active.
 *
 * Lifecycle:
 *   OPEN_DOCUMENT → handleOpenDocument (fetch + dispatch SUCCESS/FAILURE)
 *   OPEN_DOCUMENT_SUCCESS → the two pre-passes and the engine-status poller start
 *   CLOSE_DOCUMENT → those exit; the auto-save loop stays up (it is
 *     app-lifetime and simply idles without a session)
 *
 * The Redact File burn-in is NOT here. `documentBurnInSaga` (exportPush.ts) is
 * registered as its own keepAlive entry so a throw in any of the children
 * below cannot cancel a detached burn-in (VE-28061).
 */
export function* documentsSaga() {
  yield* all([
    fork(watchOpenDocument),
    fork(autoSaveSaga),
    fork(saveAllDocumentsSaga),
    fork(panelPreferencesSaga),
    fork(suspendedLocksSaga),
    fork(keywordSearchSaga),
    fork(lexiconApplySaga),
    // ORDER MATTERS at document open. Several sagas fork on
    // OPEN_DOCUMENT_SUCCESS; of those, the two below contend for the MuPDF
    // worker (neither the engine-status poller nor the lexicon apply pass
    // touches it). Sagas start in registration order, and the MuPDF
    // worker takes ONE job at a time and cannot preempt one already dispatched.
    // So whichever enqueues first pays the entire cold start — dynamic import of
    // mupdf, ~10 MB WASM instantiate, the file fetch, openDocument — and
    // everything else waits behind it. Measured on a 1602-page file with the
    // pre-pass first: its opening 25-page extract took 4.8 s of service (median
    // for the same job once warm: 247 ms), page dimensions did not publish until
    // 5.5 s, and since a page cannot raster until its size is known the first
    // page painted at 7.8 s.
    //
    // Dims first: it gates the first page render, and it is the cheaper walk
    // (~17 ms warm for 1602 pages vs a 25-page text extract). The text index
    // gates nothing the user can see for several seconds yet.
    fork(pdfDimsPrepassSaga),
    fork(pdfPrepassSaga),
    fork(onDeleteRedactedDocumentRequest),
    fork(onDownloadRedactionReport),
    fork(onIncrementRedactedDocumentDownload),
    fork(onLoadRedactedVersionPages),
    fork(auditEmitSaga),
    fork(auditLogsSaga),
    fork(redactionStatusSaga),
    fork(renameSaga),
    fork(lexiconAutoApplySaga),
    fork(watermarkAutoApplySaga),
    fork(watermarkDeriveSaga),
    fork(watermarkImageAssetSaga),
    fork(documentCommentsSaga),
    fork(runPiiDetectionSaga),
    fork(ocrStatusSaga),
    fork(engineStatusSaga),
    fork(sendToGovQaSaga),
    fork(sendToFOIAXpressSaga),
    fork(sendToCasepointSaga),
    fork(sendToExterroSaga),
    fork(sendToNuixSaga),
    fork(sendToExternalIntegrationSaga),
    fork(fetchExternalIntegrationConfigSaga),
  ]);
}

function* watchOpenDocument() {
  yield* takeLatest(OPEN_DOCUMENT, handleOpenDocument);
}

// Named exports for individual sub-sagas (useful for targeted tests)
export { handleOpenDocument } from './openDocument';
export { autoSaveSaga } from './autoSave';
export { keywordSearchSaga } from './keywordSearch';
export { lexiconApplySaga, runLexiconApply } from './lexiconApply';
export {
  documentBurnInSaga,
  onDeleteRedactedDocumentRequest,
  onDownloadRedactionReport,
  onExportRequest,
  onIncrementRedactedDocumentDownload,
  onLoadRedactedVersionPages,
} from './exportPush';
export { auditEmitSaga } from './auditEmit';
export { auditLogsSaga } from './auditLogs';
export { redactionStatusSaga } from './redactionStatus';
export { renameSaga } from './rename';
export { documentCommentsSaga } from './comments';
export { runPiiDetectionSaga } from './runPii';
export {
  fetchExternalIntegrationConfigSaga,
  sendToCasepointSaga,
  sendToExterroSaga,
  sendToExternalIntegrationSaga,
  sendToFOIAXpressSaga,
  sendToGovQaSaga,
  sendToNuixSaga,
} from './sendToIntegration';
